import { NavigationService } from "src/app/core/services/navigation.service";
import {
  BehaviorSubject,
  firstValueFrom,
  from,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from "rxjs";
import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpEventType,
  HttpHeaders,
  HttpResponse,
} from "@angular/common/http";
import { StorageDataType } from "../models/storage-data-type";
import { catchError, filter, finalize, map, takeUntil, timeout } from "rxjs/operators";
import { CopyPasteObj, FileTab, FileTabContent } from "../models/storage";
import { ManageStorageLogsService } from "./manage-storage-logs.service";
import { FileUploader } from "ng2-file-upload";
import { EnvironmentService } from "./environment.service";
import { HeaderService } from "./header.service";
import { Entities } from "../models/globals";
import { AuthenticationService } from "./authentication.service";
import { DeploymentService } from "./deployment.service";

@Injectable({
  providedIn: "root",
})
export class StorageService {
  leftPanelIndex: number;
  currentEntity: string;
  uploadProgress: number;
  uploadSub: Subscription;
  vmNodeName: boolean = false;

  constructor(
    private http: HttpClient,
    private navService: NavigationService,
    private storageLogService: ManageStorageLogsService,
    private env: EnvironmentService,
    private headerService: HeaderService,
    private deploymentService: DeploymentService,
    private authService: AuthenticationService
  ) {}

  private JobStorageDirectory = new BehaviorSubject<string>("");
  public jobStorageDirectory = this.JobStorageDirectory.asObservable(); // contains directory path of selected job in Jobs Entity

  sendJobStorageDirectoryPath(path: string) {
    this.JobStorageDirectory.next(path);
  }

  private ActiveStorage = new BehaviorSubject<string>("");
  public activeStorage = this.ActiveStorage.asObservable();

  sendActiveStorageName(storageName: string) {
    if (this.ActiveStorage.value != storageName) {
      this.ActiveStorage.next(storageName);
    }
  }

  getActiveStorageName() {
    return this.ActiveStorage.value;
  }

  private ToRefreshStorage = new BehaviorSubject<boolean>(false);
  public toRefreshStorage = this.ActiveStorage.asObservable();

  toReloadStorageFiles(value: boolean) {
    this.ToRefreshStorage.next(value);
  }

  //#region Storage Cache
  private storageCache = new BehaviorSubject<
    { path: string; data: StorageDataType[] }[]
  >(null);
  public storageCacheData = this.storageCache.asObservable();

  setStorageCacheForPath(path: string, data: StorageDataType[]) {
    if (this.storageCache.value == null) {
      this.storageCache.next([{ path: path, data: data }]);
    } else {
      let idx = this.storageCache.value.findIndex((x) => x.path == path);
      if (idx != -1) {
        this.storageCache.value[idx].data = data;
      } else {
        this.storageCache.value.push({ path: path, data: data });
      }
    }
  }

  getStorageCacheForPath(path: string) {
    return this.storageCache.value?.find((x) => x.path == path)?.data || null;
  }

  checkIfStorageCacheExists(path: string) {
    const cache = this.storageCache.value?.find((x) => x.path == path);
    return cache != null && cache.data != null;
  }

  clearStorageCache() {
    this.storageCache.next(null);
  }

  //#endregion

  //#region Use Syncstore Path cache

  private lastUsedUseSyncstorePath = new BehaviorSubject<string>(null);
  public lastUsedUseSyncstorePathData =
    this.lastUsedUseSyncstorePath.asObservable();

  setLastUsedUseSyncstorePath(path: string) {
    this.lastUsedUseSyncstorePath.next(path);
  }

  getLastUsedUseSyncstorePath() {
    return this.lastUsedUseSyncstorePath.value;
  }

  //#endregion

  //#region View File Cache

  //#endregion
  private viewFileCache = new BehaviorSubject<FileTab[]>([]);
  public viewFileCacheData = this.viewFileCache.asObservable();

  addViewFileCacheData(data: FileTab) {
    let idx = this.viewFileCache.value.findIndex(
      (x) =>
        x.filePath == data.filePath &&
        x.deploymentName == data.deploymentName &&
        x.entityType == data.entityType
    );
    if (idx == -1) {
      this.viewFileCache.value.push(data);
    }
  }
  removeViewFileCacheData(filePath: string, deploymentName: string) {
    let idx = this.viewFileCache.value.findIndex(
      (x) => x.filePath == filePath && x.deploymentName == deploymentName
    );
    if (idx != -1) {
      const filteredData = this.viewFileCache.value.splice(idx, 1);
      this.viewFileCache.next(filteredData);
    }
  }

  getViewFileCache() {
    return this.viewFileCache.value;
  }

  getNonClosedFilesData(deploymentName: string, entity: Entities) {
    return this.viewFileCache.value.filter(
      (x) =>
        x.deploymentName == deploymentName &&
        x.entityType == entity &&
        !x.closed
    );
  }

  clearViewFileCache() {
    this.viewFileCache.next(null);
  }

  getTotalLinesForFile(
    path: string,
    deploymentName: string,
    entityType: Entities
  ) {
    return this.viewFileCache.value.find(
      (x) =>
        x.filePath == path &&
        x.deploymentName == deploymentName &&
        x.entityType == entityType
    )?.totalLines;
  }

  getContentForFile(
    path: string,
    deploymentName: string,
    entityType: Entities,
    page: number
  ) {
    const content = this.viewFileCache.value.find(
      (x) =>
        x.filePath == path &&
        x.deploymentName == deploymentName &&
        x.entityType == entityType
    )?.content;
    if (content) {
      if (page == -1) {
        const maxIdx = content?.findIndex(
          (x) => x.page == Math.max(...content.map((x) => x.page))
        );
        return content[maxIdx];
      }
      return content.find((x) => x.page == page);
    }
    return null;
  }

  updateContentForFile(
    path: string,
    deploymentName: string,
    entityType: Entities,
    content: FileTabContent,
    totalLines: number
  ) {
    let idx = this.viewFileCache.value.findIndex(
      (x) => x.filePath == path && x.deploymentName == deploymentName
    );
    if (idx != -1) {
      if (content.page == -1) {
        this.viewFileCache.value[idx].content = [content];
      } else {
        let contentIdx = (
          this.viewFileCache.value[idx].content || []
        ).findIndex((x) => x.page == content.page);
        if (contentIdx && contentIdx != -1) {
          this.viewFileCache.value[idx].content[contentIdx] = content;
        } else {
          if (!this.viewFileCache.value[idx].content)
            this.viewFileCache.value[idx].content = [];
          this.viewFileCache.value[idx].content.push(content);
        }
      }
      this.viewFileCache.value[idx].totalLines = totalLines;
    }
  }

  markAsClosed(path: string) {
    let idx = this.viewFileCache.value.findIndex((x) => x.filePath == path);
    if (idx != -1) {
      this.viewFileCache.value[idx].closed = true;
    }
  }

  // api's
  getAllStoragesForUser() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getStorage(storageName: string, depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/" +
      storageName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  setStorageAlertValue(storageName: string, depName: string, alertVal: number) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/" +
      storageName +
      "/StorageFullAlert/" +
      alertVal;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    const body = { title: "Angular PUT Request" };
    this.http.put(baseUrl, body, { headers: headers }).subscribe({
      next: (data) => {},
      error: (error) => {},
    });
  }

  CreateDirectory(
    depName: string,
    storageDataType: StorageDataType,
    nodeName: string
  ) {
    try {
      if (
        storageDataType.path.includes(this.env.envVariables.syncStoreRoute) ||
        nodeName == null
      ) {
        nodeName = "";
      }
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/createdirectory";
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("VmName", nodeName);

      let options = { headers: headers };
      return this.http.post<any>(
        baseUrl,
        JSON.stringify(storageDataType),
        options
      );
    } catch (Error) {}
  }

  uploader: FileUploader;
  hasBaseDropZoneOver: boolean;
  hasAnotherDropZoneOver: boolean;
  response: string;

  UploadFile(
    depName: string,
    dirName: string,
    myFile: File,
    nodeName: string,
    uniqueId?: number
  ) {
    try {
      if (nodeName == null) {
        nodeName = "";
      }
      const formData = new FormData();
      formData.append("file", myFile);
      formData.append("destination", dirName);

      this.storageLogService.createOnPremiseUploadRequest(uniqueId);
      let timeStarted: Date = this.headerService.getUserNewDate();
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/Upload";
      let headers = new HttpHeaders();
      headers = headers
        .set("Cache-Control", "no-cache")
        .set("Access-Control-Allow-Origin", "*")
        .set(
          "Access-Control-Allow-Headers",
          "Origin, X-Requested-With, Content-Type, Accept"
        )
        .set("VmName", nodeName);

      this.http
        .post<any>(baseUrl, formData, {
          headers: headers,
          reportProgress: true,
          observe: "events",
        })
        .pipe(
          filter((file) => !!file),
          takeUntil(this.storageLogService.onPremiseUploadRequests[uniqueId])
        )
        .subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              let progress = Math.round((100 * event.loaded) / event.total);
              let timeElapsed =
                this.headerService.getUserNewDate().valueOf() -
                timeStarted.valueOf();
              let uploadSpeed = event.loaded / (timeElapsed / 1000);
              let timeRemaining = Math.round(
                (myFile.size - event.loaded) / uploadSpeed
              );
              this.storageLogService.updateTimeRemaining(
                uniqueId,
                timeRemaining
              );
              let ui_progress = `Uploaded ${progress}%`;
              this.storageLogService.updateStatusOfLog(uniqueId, ui_progress);
            } else if (event instanceof HttpResponse) {
              this.storageLogService.updateTimeRemaining(uniqueId, null);
              this.storageLogService.updateRequestCompleteFields(
                uniqueId,
                "Upload Completed"
              );
              this.toReloadStorageFiles(true);
            }
            this.storageLogService.updateStorageLogsStore();
          },
          error: () => {
            this.storageLogService.updateTimeRemaining(uniqueId, null);
            this.storageLogService.updateRequestCompleteFields(
              uniqueId,
              "Upload Failed"
            );
          },
          complete: () => {},
        });
    } catch (Error) {}
  }

  uploadCheck(name, size, type, depName) {
    this.uploader = new FileUploader({
      url: `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}StorageInfoes/Deployment/${depName}/Upload`,
      disableMultipart: true, // 'DisableMultipart' must be 'true' for formatDataFunction to be called.
      formatDataFunctionIsAsync: true,
      method: "post",
      autoUpload: true,
      formatDataFunction: async (item) => {
        return new Promise((resolve, reject) => {
          resolve({
            name: name,
            length: size,
            contentType: type,
            date: this.headerService.getUserNewDate(),
          });
        });
      },
    });

    this.hasBaseDropZoneOver = false;
    this.hasAnotherDropZoneOver = false;

    this.response = "";

    this.uploader.response.subscribe((res) => (this.response = res));
  }

  JobUploadFile(
    depName: string,
    dirName: string,
    myFile: File,
    nodeName: string
  ) {
    try {
      if (nodeName == null) {
        nodeName = "";
      }
      const formData = new FormData();
      formData.append("file", myFile);
      formData.append("destination", dirName);

      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/Upload";
      let headers = new HttpHeaders();
      headers = headers
        .set("Cache-Control", "no-cache")
        .set("Access-Control-Allow-Origin", "*")
        .set(
          "Access-Control-Allow-Headers",
          "Origin, X-Requested-With, Content-Type, Accept"
        )
        .set("VmName", nodeName);

      const upload$ = this.http
        .post<any>(baseUrl, formData, {
          headers: headers,
          reportProgress: true,
          observe: "events",
        })
        .pipe(finalize(() => {}));

      return upload$;
    } catch (Error) {}
  }

ListFiles(dirPath: string, depName: string, nodeName?: string, forTreeView: boolean = false): Observable<StorageDataType[]> {
    return from(this.listFilesAsync(dirPath, depName, nodeName, forTreeView));
  }

  private async listFilesAsync(dirPath: string, depName: string, nodeName?: string, forTreeView: boolean = false): Promise<StorageDataType[]> {
    try {
      const currentUser = this.authService.currentUserValue?.username?.split("@")[0];
      
      const newApiPromise = this.ListFilesNew(dirPath, depName, nodeName, currentUser);
      const oldApiPromise = firstValueFrom(this.ListFilesOld(dirPath, depName, nodeName, forTreeView));

      // Race them - whichever completes first returns
      return await Promise.race([newApiPromise, oldApiPromise]);
      
    } catch (error) {
      console.error('Error fetching files:', error);
      return await firstValueFrom(this.ListFilesOld(dirPath, depName, nodeName, forTreeView));
    }
  }

  private async ListFilesNew(dirPath: string, depName: string, nodeName: string | undefined, currentUser: string | undefined): Promise<StorageDataType[]> {
    let headNodeIp = this.deploymentService.getHeadNodeIpForDeployment(depName);
    
    if (!headNodeIp) {
      const data: any = await firstValueFrom(this.getHeadNodeIp(depName));
      headNodeIp = data?.vmIp;
    }
    
    if (!headNodeIp) {
      throw new Error('No head node IP available');
    }

    const baseUrl = `${this.env.envVariables.httpProtocol}${headNodeIp}:${this.env.envVariables.UtilityAccessPort}/list-files`;
    const headers = new HttpHeaders()
      .set('dirpath', dirPath)
      .set('vmname', nodeName || '')
      .set('userid', currentUser || 'root')
      .set('islocalstore', this.deploymentService.isLocalStoreEnabled(depName).toString());

    const response = await firstValueFrom(
      this.http.post<any>(baseUrl, {}, { headers }).pipe(
        timeout(30000)
      )
    );
    
    if (!response?.success) {
      throw new Error('Invalid response from new API');
    }
    
    return response.items as StorageDataType[];
  }

  ListFilesOld(dirPath: string, depName: string, nodeName?: string, forTreeView: boolean = false) {
    try {
      let baseUrl;
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("ForTreeView", String(forTreeView))
        .set("Deployment", depName);

      if (this.vmNodeName && nodeName != null) {
        baseUrl =
          this.env.envVariables.httpProtocol +
          this.env.envVariables.webHost +
          "StorageInfoes/Deployment/" +
          depName +
          "/VmNodeList";
        headers = headers.append("VmName", nodeName);
      } else {
        baseUrl =
          this.env.envVariables.httpProtocol +
          this.env.envVariables.webHost +
          "StorageInfoes/Deployment/" +
          depName +
          "/NodeList";
      }

      let options = { headers: headers };

      var jobData: StorageDataType = {
        name: "",
        type: "",
        path: dirPath,
      };

      // const a = '[{"name":"abmore_2204","type":"directory","path":"/mnt/syncstore/hpcadmin/abmore_2204","owner":"hpcadmin","date":"2024-09-04 11:24:26","size":"0"},{"name":"abmore_2205","type":"directory","path":"/mnt/syncstore/hpcadmin/abmore_2205","owner":"hpcadmin","date":"2024-09-04 11:45:38","size":"0"},{"name":"SyncHpcCore.cs","type":"file","path":"/mnt/syncstore/hpcadmin/SyncHpcCore.cs","owner":"hpcadmin","date":"2024-09-25 06:22:24","size":"203073"},{"name":"uploadRename.txt","type":"file","path":"/mnt/syncstore/hpcadmin/uploadRename.txt","owner":"hpcadmin","date":"2024-09-25 12:03:34","size":"81"}]'

      // return of(JSON.parse(a));
      return this.http.post<any>(baseUrl, JSON.stringify(jobData), options);
    } catch (Error) {}
  }

  DeleteData(
    depName: string,
    storageDataType: StorageDataType,
    nodeName: string
  ) {
    try {
      this.navService.index.subscribe((x) => (this.leftPanelIndex = x));
      this.navService.breadcrumbEntity.subscribe((x) => {
        this.currentEntity = x;
      });
      if (
        storageDataType.path.includes(this.env.envVariables.syncStoreRoute) ||
        nodeName == null
      ) {
        nodeName = "";
      }
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/delete";
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("VmName", nodeName)
        .set("Deployment", depName);

      return this.http.put(baseUrl, JSON.stringify(storageDataType), {
        headers: headers,
      });
    } catch (Error) {}
  }

  DownloadFile(
    depName: string,
    fileName: string,
    path: string,
    nodeName: string
  ): Observable<HttpResponse<any>> {
    if (
      path.includes(this.env.envVariables.syncStoreRoute) ||
      nodeName == null
    ) {
      nodeName = "";
    }
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Deployment/" +
      depName +
      "/Download?file=" +
      fileName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("VmName", nodeName)
      .set("Path", path);
    return this.http.get(baseUrl, {
      headers: headers,
      observe: "response",
      responseType: "blob",
    });
  }

  DownloadFolder(
    depName: string,
    folderName: string,
    path: string,
    nodeName: string
  ): Observable<HttpResponse<any>> {
    if (
      path.includes(this.env.envVariables.syncStoreRoute) ||
      nodeName == null
    ) {
      nodeName = "";
    }
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Deployment/" +
      depName +
      "/ZipDownload?folderName=" +
      folderName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName)
      .set("VmName", nodeName)
      .set("Path", path);
    return this.http.get(baseUrl, {
      headers: headers,
      observe: "response",
      responseType: "blob",
    });
  }

  viewFileData(
    depName: string,
    path: string,
    nodeName: string,
    pageObj: any,
    convertToHtml: boolean = true
  ) {
    try {
      if (path.includes("/mnt/syncstore") || nodeName == null) {
        nodeName = "";
      }
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/viewdata";
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("Deployment", depName)
        .set("VmName", nodeName)
        .set("ConvertToHtml", String(convertToHtml))
        .set("Path", path)
        .set("TotalLines", pageObj.totalLines.toString())
        .set("PageSize", pageObj.pageSize.toString())
        .set("CurrentIndex", pageObj.currentIndex.toString());
      return this.http.get(baseUrl, { headers: headers });
    } catch (e) {}
  }

  writeFileData(depName: string, path: string, nodeName: string, fileData) {
    try {
      if (
        path.includes(this.env.envVariables.syncStoreRoute) ||
        nodeName == null
      ) {
        nodeName = "";
      }

      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/setdata";
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json")
        .set("Cache-Control", "no-cache")
        .set("Deployment", depName)
        .set("VmName", nodeName)
        .set("Path", path);

      return this.http
        .post<any>(baseUrl, JSON.stringify(fileData), { headers: headers })
        .subscribe(
          (res) => {},
          (err) => {}
        );
    } catch (e) {}
  }

  copyPasteFile(depName: string, dataObject: CopyPasteObj, nodeName?: string) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/CopyData";
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("Deployment", depName)
        .set("VmName", nodeName ? nodeName : "");

      return this.http.put(baseUrl, dataObject, { headers: headers, responseType: 'text' })
    } catch (error) {
    }
  }

  cutPasteFile(depName: string, dataObject: CopyPasteObj, nodeName?: string) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/copyFile";
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("Deployment", depName);

      return this.http.put(baseUrl, dataObject, { headers: headers });
    } catch (error) {}
  }

  getSyncStoreUsage(depName: string, storageName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Deployment/" +
      depName +
      "/SyncstoreUsage/" +
      storageName;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.get(baseUrl, { headers: headers });
  }

  getWorkStoreUsage(depName: string, storageName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Deployment/" +
      depName +
      "/WorkstoreUsage/" +
      storageName;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.get(baseUrl, { headers: headers });
  }

  getVmIP(depName: string, nodeName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "VmInfoes/Deployment/" +
      depName +
      "/StorageNodeName=" +
      nodeName +
      "/GetIp";

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.get(baseUrl, { headers: headers });
  }

  rename(
    depName: string,
    storageDataType: StorageDataType,
    clikedFilePath: string,
    nodeName: string
  ) {
    try {
      if (
        storageDataType.path.includes(this.env.envVariables.syncStoreRoute) ||
        nodeName == null
      ) {
        nodeName = "";
      }
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "StorageInfoes/Deployment/" +
        depName +
        "/rename";
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("PrevPath", clikedFilePath)
        .set("VmName", nodeName);

      let options = { headers: headers };
      return this.http.post<any>(
        baseUrl,
        JSON.stringify(storageDataType),
        options
      );
    } catch (Error) {}
  }

  getStorageSasShare(depName: string, storageName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Deployment/" +
      depName +
      "/GetSasShare";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Storage", storageName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getHeadNodeIp(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "VmInfoes/Deployment/" +
      depName +
      "/GetHeadNodeIp";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  checkIsVdi(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/CheckIfDeploymentVDI";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  uploadToWorkStore(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Deployment/" +
      depName +
      "/UploadToWorkStore";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  zipUnzipStorageData(
    depName: string,
    folderName: string,
    path: string,
    nodeName: string,
    zip: boolean,
    zipName: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `StorageInfoes/Deployment/${depName}/ZipUnzipFolder/${folderName}`;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Path", path)
      .set("VmName", nodeName ? nodeName : "")
      .set("ZipName", zipName)
      .set("toZip", zip.toString());

    return this.http.get(baseUrl, { headers: headers });
  }

  zipWithService(
    filePaths: string,
    zipPath: string,
    zipName: string,
    nodeName: string,
    vmIp: string,
    userId: string
  ): Observable<any> {
    return new Observable(observer => {
      const baseUrl = `${this.env.envVariables.httpProtocol}${vmIp}:5004/zip`;

      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "text/plain; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("zipPath", zipPath)
        .set("VmName", nodeName ? nodeName : "")
        .set("ZipName", zipName)
        .set("userid", userId);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', baseUrl, true);
      
      headers.keys().forEach(key => {
        xhr.setRequestHeader(key, headers.get(key));
      });

      let lastProcessedLength = 0;
      
      xhr.onprogress = () => {
        const newData = xhr.responseText.substring(lastProcessedLength);
        lastProcessedLength = xhr.responseText.length;
        
        if (!newData) return;

        const messages = newData.split('\n\n');
        
        messages.forEach(message => {
          if (!message.trim()) return;
          
          const jsonStr = message.replace(/^data:\s*/gm, '').trim();
          
          if (!jsonStr) return;
          
          try {
            const data = JSON.parse(jsonStr);
            
            if (data.type === 'started') {
              observer.next({ type: 'started', percentage: data.percentage });
            } else if (data.type === 'progress') {
              observer.next({ type: 'progress', percentage: data.percentage });
            } else if (data.type === 'complete') {
              if (data.success) {
                observer.next({ type: 'complete', success: true, percentage: 100 });
                observer.complete();
              } else {
                observer.error(new Error(data.message || 'Zip failed'));
              }
            } else if (data.type === 'cancelled') {
              observer.error({ name: 'AbortError', message: 'Cancelled' });
            }
          } catch (e) {
            console.error('Failed to parse SSE message:', jsonStr, e);
          }
        });
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (!observer.closed) {
            observer.complete();
          }
        } else {
          observer.error(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        observer.error(new Error('Network error'));
      };

      xhr.onabort = () => {
        observer.error({ name: 'AbortError', message: 'Request aborted' });
      };

      xhr.send(filePaths); 

      return () => {
        xhr.abort();
      };
    });
  }

  getZipJobStatus(jobId: string, vmIp: string): Observable<any> {
    console.log("getZipJobStatus.......");
    console.log("vmip ", vmIp);
    const baseUrl =
      this.env.envVariables.httpProtocol + vmIp + `:5004/zip/status/${jobId}`;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.get(baseUrl, { headers: headers });
  }

  // New method to cancel zip job
  cancelZipJob(jobId: string, vmIp: string): Observable<any> {
    const baseUrl =
      this.env.envVariables.httpProtocol + vmIp + `:5004/zip/cancel`;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("jobid", jobId);

    return this.http.post(baseUrl, { jobId: jobId }, { headers: headers });
  }

  ViewFileWithService(path: string, nodeName: string, vmIp: string, data: any) {
    let baseUrl = this.env.envVariables.httpProtocol + vmIp + `:5004/read_file`;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Path", path);

    return this.http.post(baseUrl, data, { headers: headers });
  }

  checkIfFileFolderExists(
    path: string,
    nodeName: string,
    vmIp: string
  ): Observable<any> {
    const baseUrl = `${this.env.envVariables.httpProtocol}${vmIp}:${this.env.envVariables.UtilityAccessPort}/checkPath`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Cache-Control", "no-cache")
      .set("Content-Type", "application/json; charset=utf-8")
      .set("VmName", nodeName || "") // Use the nullish coalescing operator for default value
      .set("Path", path);
    return this.http.get(baseUrl, { headers: headers }).pipe(
      map((res: any) => JSON.parse(res?.message?.toLowerCase())),
      catchError(this.handleError) // Handle errors using catchError
    );
  }

  private handleError(error: any): Observable<never> {
    console.error("An error occurred:", error);
    return throwError(() => "Something went wrong. Please try again later.");
  }

  getStorageData(name: string): Observable<any> {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Data/" +
      name;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getStorageJson(cloudValue: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Details";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("CloudValue", cloudValue);
    return this.http.get(baseUrl, { headers: headers });
  }

  createStorage(storageData: any, cloudValue) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Create";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("CloudValue", cloudValue);

    return this.http.post(baseUrl, storageData, { headers: headers });
  }

  updateStorage(storageData: any, cloudValue) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Update";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("CloudValue", cloudValue);

    return this.http.put(baseUrl, storageData, { headers: headers });
  }

  deleteStorage(name: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/Delete/" +
      name;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.delete(baseUrl, { headers: headers });
  }

  checkIfUtilityServiceIsRunning(depName: string, userId: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "StorageInfoes/CheckUtiltyService";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    headers = headers.set("UserId", userId);
    headers = headers.set("depName", depName);
    return this.http.get(baseUrl, { headers: headers, responseType: "text" });
  }

  getUnzipJobStatus(jobId: string, vmIp: string): Observable<any> {
    console.log(`Getting unzip status for job: ${jobId} from VM: ${vmIp}`);
    const baseUrl = `${this.env.envVariables.httpProtocol}${vmIp}:5004/unzip/status/${jobId}`;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")

    return this.http.get(baseUrl, { headers: headers }).pipe(
      catchError((error) => {
        console.error(`Error getting unzip status for job ${jobId}:`, error);
        if (error.status === 404) {
          return of({ success: false, status: "completed", message: "Job not found, likely completed" });
        }
        return of({ success: false, status: "failed", error: error.message });
      })
    );
  }

  // Method to cancel unzip job
  cancelUnzipJob(jobId: string, vmIp: string): Observable<any> {
    const baseUrl = `${this.env.envVariables.httpProtocol}${vmIp}:5004/unzip/cancel`;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("jobid", jobId)
    return this.http.post(baseUrl, {}, { headers: headers }).pipe(
      catchError((error) => {
        console.error(`Error cancelling unzip job ${jobId}:`, error);
        return throwError(() => error);
      })
    );
  }
  // Enhanced unZipWithService method with better header names and userid support
  unZipWithService(
    zipFilePath: string,
    extractPath: string,
    nodeName: string,
    vmIp: string,
    userId: string,
    forceOverwrite: boolean = false
  ): Observable<any> {
    return new Observable(observer => {
      const baseUrl = `${this.env.envVariables.httpProtocol}${vmIp}:5004/unzip`;

      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache")
        .set("zipfilepath", zipFilePath)
        .set("extractpath", extractPath)
        .set("vmname", nodeName ? nodeName : "null")
        .set("userid", userId)
        .set("forceoverwrite", forceOverwrite.toString());

      const xhr = new XMLHttpRequest();
      xhr.open('POST', baseUrl, true);
      
      headers.keys().forEach(key => {
        xhr.setRequestHeader(key, headers.get(key));
      });

      let lastProcessedLength = 0;
      
      xhr.onprogress = () => {
        const newData = xhr.responseText.substring(lastProcessedLength);
        lastProcessedLength = xhr.responseText.length;
        
        if (!newData) return;

        const messages = newData.split('\n\n');
        
        messages.forEach(message => {
          if (!message.trim()) return;
          
          const jsonStr = message.replace(/^data:\s*/gm, '').trim();
          if (!jsonStr) return;
          
          try {
            const data = JSON.parse(jsonStr);
            
            if (data.type === 'started') {
              observer.next({ type: 'started', percentage: data.percentage });
            } else if (data.type === 'progress') {
              observer.next({ type: 'progress', percentage: data.percentage });
            } else if (data.type === 'complete') {
              if (data.success) {
                observer.next({ type: 'complete', success: true, percentage: 100 });
                observer.complete();
              } else {
                observer.error(new Error(data.message || 'Unzip failed'));
              }
            } else if (data.type === 'cancelled') {
              observer.error({ name: 'AbortError', message: 'Unzip cancelled' });
            } else if (data.type === 'error') {
              observer.error(new Error(data.message || 'Unzip error'));
            }
          } catch (e) {
            console.error('Failed to parse unzip SSE:', jsonStr, e);
          }
        });
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (!observer.closed) {
            observer.complete();
          }
        } else {
          observer.error(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        observer.error(new Error('Network error'));
      };

      xhr.onabort = () => {
        observer.error({ name: 'AbortError', message: 'Request aborted' });
      };

      xhr.send(null);

      return () => {
        if (xhr.readyState !== XMLHttpRequest.DONE) {
          xhr.abort();
        }
      };
    });
  }
}
